import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { convertInstallWindowToReadableString } from '../../../common/common';
import { useCartData, useScheduleDate } from '../../../hooks/order';
import { buildSlotsByDate, parseRawScheduleDateTime, formatLocalDate } from './scheduleUtils';
import MobileDateTimePicker from './MobileDateTimePicker';
import DesktopDateTimePicker from './DesktopDateTimePicker';
import AppointmentSkeleton from './AppointmentSkeleton';

const InstallationAppointmentSection = ({ activeKey, setActiveKey, scheduleAppt, isMobileAdded, isMobile, isLoading }) => {
  const { t } = useTranslation();
  const { scheduleDateTime, rawScheduleDateTime } = useScheduleDate();
  const { spanishLanguagePref } = useCartData();
  const isActive = activeKey === '1';
  const isDone = parseInt(activeKey, 10) > 1;
  const { date: savedDate, time: savedTime } = useMemo(
    () => parseRawScheduleDateTime(rawScheduleDateTime),
    [rawScheduleDateTime]
  );

  const formattedSummary = useMemo(() => {
    if (!savedDate) {
      if (!scheduleDateTime) {return '';}
      const readable = convertInstallWindowToReadableString(scheduleDateTime);
      return readable.replace(/\s+between\s+/gi, ' · ').replace(/\s+and\s+/gi, ' – ');
    }
    const dateFormatted = formatLocalDate(savedDate, 'ddd, MMM D', spanishLanguagePref);
    const timeFormatted = savedTime ? savedTime.replace(/\s+to\s+/gi, ' – ') : '';
    return `${dateFormatted} · ${timeFormatted}`;
  }, [savedDate, savedTime, scheduleDateTime, spanishLanguagePref]);

  // activeKey is only ever non-empty once a plan has been confirmed (SinglePageCheckout.js's
  // productConfirmed effect is what first sets it) - before that, this header must stay inert
  // so this step can't be opened ahead of plan selection. Also false while this section is
  // itself the one currently expanded (isActive) - otherwise the "Change" link renders right
  // above the very picker it would reopen - and while appointment windows are still loading.
  const isInteractive = Boolean(activeKey) && !isActive && !isLoading;

  // "First Available" badge: true when the saved slot is still the earliest date/time in
  // scheduleAppt, i.e. the customer never changed the auto-selected default. Derived fresh
  // from the same data each render rather than a separate flag, so it survives collapse/reopen.
  const isFirstAvailable = useMemo(() => {
    if (!rawScheduleDateTime) {return false;}
    const slotsByDate = buildSlotsByDate(scheduleAppt);
    const dates = Object.keys(slotsByDate);
    const firstDate = dates[0];
    const firstSlot = slotsByDate[firstDate]?.[0];
    return Boolean(firstDate) && savedDate === firstDate && savedTime === firstSlot;
  }, [scheduleAppt, rawScheduleDateTime, savedDate, savedTime]);

  return (
    <div className={`spc-card spc-appointment-section ${isActive || isLoading ? 'is-expanded' : 'is-collapsed'}`}>
      <div
        className={`spc-card__collapsed-row ${isInteractive ? 'spc-card__collapsed-row--clickable' : ''}`}
        onClick={() => isInteractive && setActiveKey('1')}
      >
        <div className="spc-collapsed-plan spc-collapsed-appointment">
          <span className="spc-collapsed-plan__title">
            {t('checkout.installationAppt')}
          </span>
          {isDone && !isLoading && formattedSummary && (
            <div className="spc-collapsed-appointment__summary-row">
              <span className="spc-collapsed-plan__summary">{formattedSummary}</span>
              {isFirstAvailable && (
                <span className="spc-first-available-badge">
                  {t('singlePageCheckout.firstAvailable')}
                </span>
              )}
            </div>
          )}
        </div>
        {isInteractive && (
          <button type="button" className="spc-link-button spc-change-link" onClick={() => setActiveKey('1')}>
            {t('common.change')}
          </button>
        )}
      </div>

      {/* isLoading is scheduleLoading - CheckScheduleAppt, dispatched by SinglePageCheckout.js
          right after plan confirmation (or by the RTS modal's retry). A skeleton of the picker
          holds its place until the appointment windows arrive, instead of a full-page loader. */}
      {isLoading ? (
        <AppointmentSkeleton isMobile={isMobile} />
      ) : isActive && (
        isMobile
          ? <MobileDateTimePicker scheduleAppt={scheduleAppt} isMobileAdded={isMobileAdded} setActiveKey={setActiveKey} />
          : <DesktopDateTimePicker scheduleAppt={scheduleAppt} isMobileAdded={isMobileAdded} setActiveKey={setActiveKey} />
      )}
    </div>
  );
};

export default InstallationAppointmentSection;
